import React from "react"
import PropTypes from "prop-types"
import moment from "moment"
import RoomStatus from "../RoomStatusBsr"
import Loading from "../Loading"
import "./RoomStatusListBsr.scss"
import FontAwesome from "react-fontawesome"
import { NO_DATA_BLURB } from "@swipesense/compliance-helpers"
import {
  Table,
  AutoSizer,
  Column,
  defaultTableRowRenderer,
  SortDirection,
} from "react-virtualized"
import {
  noOccupancy,
  noRNDetected,
  noVisitsDetected,
} from "../../helpers/sharedHelperFunctions"
import RoomStatusBsrWithData from "../RoomStatusBsr/RoomStatusBsrWithData"
import { Status } from "@swipesense/shared_components_v2/dist/index"

export default class RoomStatusListBsr extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      selectedIndex: -1,
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.table.recomputeRowHeights()
  }

  displaysWarningClass = shift => {
    /* istanbul ignore next */
    return `none-detected${this.displaysWarning(shift) ? "__alert" : ""}`
  }

  displaysWarning = shift => {
    return (
      (noVisitsDetected(shift) || noRNDetected(shift)) && !noOccupancy(shift)
    )
  }

  locationCellRenderer = ({ cellData }) => {
    return <div key="name">{cellData.name}</div>
  }

  nameCellRenderer = ({ cellData, rowData }) => {
    if (cellData) {
      const name = cellData.firstName + " " + cellData.lastName
      return <div key="name">{name}</div>
    } else if (noOccupancy(rowData)) {
      return <div className="no-occupancy">room not occupied</div>
    } else {
      /* istanbul ignore next */
      return (
        <div className={this.displaysWarningClass(rowData)}>none detected</div>
      )
    }
  }

  dateCellRenderer = ({ cellData }) => {
    return <div key="shift">{moment(cellData).format("M/D/YYYY HH:mm")}</div>
  }

  bsrComplianceCellRenderer = ({ rowData }) => {
    const result = []
    if (rowData.beginningShiftChange) {
      if (rowData.beginningShiftChange.complianceMet) {
        result.push(
          <span>
            <span className="compliant no-display-mobile">
              <Status className={"success-alt"} text={"Success"} />
            </span>
            <span className="compliant no-display-desktop">
              <Status className={"success-alt"} text={""} />
            </span>
          </span>
        )
      } else {
        if (rowData.beginningShiftChange.duration > 0) {
          result.push(
            <span>
              <span className="partial-compliant no-display-mobile">
                <Status className={"warning-alt"} text={"Attempt Made"} />
              </span>
              <span className="partial-compliant no-display-desktop">
                <Status className={"warning-alt"} text={""} />
              </span>
            </span>
          )
        } else {
          result.push(
            <span>
              <span className="non-compliant no-display-mobile">
                <Status className={"error-alt"} text={"No Attempt"} />
              </span>
              <span className="non-compliant no-display-desktop">
                <Status className={"error-alt"} text={""} />
              </span>
            </span>
          )
        }
      }
    } else {
      result.push(
        <span className="none-detected">
          <span>
            <div className="no-display-mobile">
              <Status text="N/A" />
            </div>
            <div className="no-display-desktop">
              <Status text={"N/A"} />
            </div>
          </span>
        </span>
      )
    }

    if (rowData.endingShiftChange) {
      if (rowData.endingShiftChange.complianceMet) {
        result.push(
          <span>
            <span className="compliant no-display-mobile">
              <Status className={"success-alt"} text={"Success"} />
            </span>
            <span className="compliant no-display-desktop">
              <Status className={"success-alt"} text={""} />
            </span>
          </span>
        )
      } else {
        if (rowData.endingShiftChange.duration > 0) {
          result.push(
            <span>
              <span className="partial-compliant no-display-mobile">
                <Status className={"warning-alt"} text={"Attempt Made"} />
              </span>
              <span className="partial-compliant no-display-desktop">
                <Status className={"warning-alt"} text={""} />
              </span>
            </span>
          )
        } else {
          result.push(
            <span>
              <span className="non-compliant no-display-mobile">
                <Status className={"error-alt"} text={"No Attempt"} />
              </span>
              <span className="non-compliant no-display-desktop">
                <Status className={"error-alt"} text={""} />
              </span>
            </span>
          )
        }
      }
    } else {
      result.push(
        <span>
          <span className="none-detected no-display-mobile">
            <Status text="N/A" />
          </span>
          <span className="none-detected no-display-desktop">
            <Status text={"N/A"} />
          </span>
        </span>
      )
    }
    return <div className="bsr-indicators">{result}</div>
  }

  calculateExpandedRowHeight = (index, selectedIndex, rowData) => {
    if (index !== selectedIndex) return 0
    else if (noVisitsDetected(rowData)) return 50
    else return 250
  }

  roomStatusRowRenderer = (props, selectedIndex) => {
    const { index, rowData, style, key } = props
    if (index === selectedIndex) {
      return (
        <div
          style={{ ...style, display: "flex", flexDirection: "column" }}
          key={key}
        >
          {defaultTableRowRenderer({
            ...props,
            style: { width: style.width, height: 36 },
          })}
          <div
            style={{
              width: style.width,
              height: this.calculateExpandedRowHeight(
                index,
                selectedIndex,
                rowData
              ),
            }}
          >
            {!noVisitsDetected(rowData) && (
              <RoomStatusBsrWithData
                locationId={rowData.location.id}
                startDate={rowData.startsAt}
                endDate={rowData.endsAt}
                shiftAssignmentId={rowData.id}
              />
            )}
            {(noRNDetected(rowData) || noVisitsDetected(rowData)) && (
              <div className="no-rn-note">
                Note: Bedside Shift Report Compliance and Nurse Rounding
                Compliance are only calculated for patients where a Nurse has
                been detected visiting the patient. A Nurse may not be detected
                if they are not wearing their SwipeSense badge, or their badge
                is no longer functioning. Please contact your program manager or{" "}
                <a href="mailto:support@swipesense.com">
                  support@swipesense.com
                </a>{" "}
                if you believe this is an error.
              </div>
            )}
          </div>
        </div>
      )
    } else {
      return defaultTableRowRenderer(props)
    }
  }

  indexRenderer = ({ rowIndex, rowData }, selectedIndex) => {
    if (!noOccupancy(rowData)) {
      if (rowIndex !== selectedIndex) {
        return <FontAwesome name="caret-right" />
      } else {
        return <FontAwesome name="caret-down" />
      }
    }
  }

  render() {
    return (
      <div style={{ flex: 1, height: "100%" }}>
        <AutoSizer>
          {({ height, width }) => (
            <Table
              className="RoomStatusListBsr"
              rowHeight={({ index }) =>
                this.calculateExpandedRowHeight(
                  index,
                  this.state.selectedIndex,
                  this.props.shiftAssignments[index]
                ) + 50
              }
              headerHeight={32}
              height={height || 800}
              width={width || 1500}
              rowCount={this.props.shiftAssignments.length}
              rowGetter={({ index }) => this.props.shiftAssignments[index]}
              sortBy={"startsAt"}
              data={this.props.shiftAssignments}
              loading={this.props.loading}
              onRowClick={props =>
                this.setState(state => {
                  if (
                    state.selectedIndex === props.index ||
                    noOccupancy(props.rowData)
                  ) {
                    return { selectedIndex: -1 }
                  } else {
                    return { selectedIndex: props.index }
                  }
                })
              }
              noRowsMessage={NO_DATA_BLURB}
              loadingComponent={<Loading />}
              rowRenderer={props =>
                this.roomStatusRowRenderer(props, this.state.selectedIndex)
              }
              ref={ref => (this.table = ref)}
            >
              <Column
                dataKey="index"
                width={15}
                label=""
                cellRenderer={props =>
                  this.indexRenderer(props, this.state.selectedIndex)
                }
                defaultSortDirection={SortDirection.ASC}
              />
              {this.props.entityType !== "location" && (
                <Column
                  dataKey="location"
                  width={10}
                  flexGrow={1}
                  label="Location"
                  cellRenderer={this.locationCellRenderer}
                  defaultSortDirection={SortDirection.ASC}
                />
              )}
              <Column
                dataKey="startsAt"
                width={15}
                flexGrow={1}
                label="Shift Start"
                cellRenderer={this.dateCellRenderer}
                defaultSortDirection={SortDirection.DESC}
              />
              <Column
                dataKey="endsAt"
                width={15}
                flexGrow={1}
                label="Shift End"
                cellRenderer={this.dateCellRenderer}
                defaultSortDirection={SortDirection.DESC}
              />
              <Column
                dataKey="primaryUser"
                width={20}
                flexGrow={1}
                label="Nurse"
                cellRenderer={this.nameCellRenderer}
                defaultSortDirection={SortDirection.ASC}
              />
              <Column
                dataKey="compliancePercentage"
                width={10}
                flexGrow={1}
                label="BSR Compliance"
                cellRenderer={this.bsrComplianceCellRenderer}
                defaultSortDirection={SortDirection.DESC}
              />
            </Table>
          )}
        </AutoSizer>
      </div>
    )
  }
}

RoomStatusListBsr.propTypes = {
  shiftAssignments: PropTypes.arrayOf(RoomStatus.propTypes.shiftAssignment)
    .isRequired,
  loading: PropTypes.bool,
  entityType: PropTypes.string,
}

RoomStatusListBsr.defaultProps = {
  ignoreOccupancy: false,
}
